<template>
    <div class="rednax-table" :class="{ 'card': !noWrapper }">
        <div v-if="$slots.header" class="card-header header-elements-inline bg-white">
            <slot name="header"></slot>
        </div>
        <div v-if="$slots.default" class="card-body">
            <slot></slot>
        </div>
        <div class="mb-0 table-responsive" :class="{ 'loading': busy_loading, 'border-top': !noWrapper }">
            <table class="table table-striped" :class="['table-' + size]">
                <thead>
                    <tr>
                        <th v-for="(col, c_index) in columns" :key="c_index" :class="{ 'text-right': !!col.align_right }">
                            <div v-if="col.sortable" @click="setSortBy(col.data)" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                <div v-html="col.header" class="flex-grow-1" v-bind:class="{ 'text-right': !!col.align_right }"></div>
                                <div class="div-table-th-sort">
                                    <i v-if="sort_by_column===col.data && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                    <i v-else-if="sort_by_column===col.data && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                    <i v-else class="inactive icon-arrow-resize8"></i>
                                </div>
                            </div>
                            <template v-else>
                                {{ col.header }}
                            </template>
                        </th>
                    </tr>
                </thead>
                <tbody v-if="local_data.data && local_data.data.length>0">
                    <tr v-for="(row, r_index) in local_data.data" :key="r_index" :class="parseRowClasses(rowClass, row)">
                        <td
                            v-for="(col, c_index) in columns"
                            :key="c_index" @click="rowClick($event, row, col)"
                            :class="[{ 'cursor-pointer': has_row_click_listener, 'text-right': !!col.align_right }].concat((col.class ? parseColClasses(col.class, row) : []))"
                        >
                            <slot v-if="$scopedSlots[`cell_${col.data}`]" :name="`cell_${col.data}`" :cell="(col.data in row ? row[col.data] : '')" :row="row"></slot>
                            <span v-else-if="col.render && typeof col.render === 'function'" v-html="col.render(row)"></span>
                            <span v-else>{{ (col.data in row ? row[col.data] : '') }}</span>
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr>
                        <td :colspan="columns.length">
                            <div class="p-4 text-center"><i>Ingen data...</i></div>
                        </td>
                    </tr>
                </tbody>
                <tfoot v-if="$slots.tfoot">
                    <slot name="tfoot"></slot>
                </tfoot>
            </table>
        </div>
        <div v-if="$slots.footer" class="card-footer bg-white">
            <slot name="footer"></slot>
        </div>
        <div v-else-if="$scopedSlots.footer" class="card-footer bg-white">
            <slot name="footer" :meta="local_data.meta" :btnSearchPreviousPage="btnSearchPreviousPage" :btnSearchNextPage="btnSearchNextPage"></slot>
        </div>
        <div v-else-if="local_data.meta" class="card-footer bg-white">
            <div class="d-flex flex-column flex-md-row align-items-center">
                <div class="text-center text-md-left">
                    <p class="mb-0">Visar {{ local_data.meta.from }} - {{ local_data.meta.to }} av {{ local_data.meta.total }} rader</p>
                </div>
                <div class="text-center text-md-right ml-md-auto mb-0 d-flex">
                    <slot name="buttons"></slot>
                    <div v-if="!hidePerPage" style="width: 140px; margin-right: 10px;">
                        <vue-select
                            v-model="per_page"
                            :reduce="(opt) => opt.id"
                            :options="select_per_page"
                            :clearable="false"
                        ></vue-select>
                    </div>
                    <nav>
                        <ul class="pagination justify-content-center">
                            <li class="page-item" v-bind:class="{disabled:page<=1}">
                                <a class="page-link" @click.prevent="btnSearchPreviousPage" href="#">←   Föregående</a>
                            </li>
                            <li class="page-item active">
                                <span class="page-link">Sida {{ page }} av {{ local_data.meta.last_page }}</span>
                            </li>
                            <li class="page-item" v-bind:class="{disabled:page>=local_data.meta.last_page}">
                                <a class="page-link" @click.prevent="btnSearchNextPage" href="#">Nästa   →</a>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { debounce, orderBy } from 'lodash';
import apiClient from '@services/ApiClient';
import { getUrlParameters } from '@services/Helpers';
import VueSelect from '@components/Wrappers/VueSelect.vue';

export default {
    name: 'RednaxDatatable',
    components: {
        VueSelect,
    },
    props: {
        size: {
            type: String,
            default: 'xs',
        },
        url: {
            type: String,
        },
        columns: {
            type: Array,
            required: true,
        },
        data: {
            type: Array,
        },
        filter: {
            type: Object,
            default() {
                return {};
            },
        },
        perPage: {
            type: Number,
            default: 25,
        },
        hidePerPage: {
            type: Boolean,
            default: false,
        },
        showPerPageAll: {
            type: Boolean,
            default: false,
        },
        sortBy: {
            type: String,
            default: '',
        },
        sortOrder: {
            type: String,
            default: 'asc',
        },
        noWrapper: {
            type: Boolean,
            default: false,
        },
        loadOnInit: {
            type: Boolean,
            default: true,
        },
        rowClass: {
            type: [Array, String, Function],
            default: null,
        },
        autoSearch: {
            type: Boolean,
            default: true,
        },
    },
    watch: {
        filter: {
            deep: true,
            handler(filter) {
                if (!this.navigating && this.autoSearch) {
                    this.local_filter = filter;
                    this.page = 1;
                    this.search(true);
                }
            },
        },
        local_filter: {
            deep: true,
            handler(filter) {
                if (this.history) {
                    this.$emit('updateFilter', filter);
                }
            },
        },
        perPage(newVal) {
            if (!this.navigating) {
                this.page = 1;
                this.per_page = newVal;
                this.search(true);
            }
        },
        url() {
            this.page = 1;
            this.search(true);
        },
        data: {
            deep: true,
            handler() {
                this.search();
            },
        },
    },
    data: () => ({
        busy_loading: false,
        local_data: [],
        local_filter: {},
        page: 1,
        per_page: 25,
        sort_by_column: '',
        sort_by_order: '',
        sort_clicktimer: null,
        sort_clicks: 0,
        search: debounce(function search(setHistory = false) {
            const vm = this;
            vm.busy_loading = true;
            if (this.url) {
                this.$emit('data:loading', true);
                if (this.history && setHistory) {
                    this.setSearchHistory();
                }
                const filter = {
                    ...this.local_filter, page: this.page, sort_by_column: this.sort_by_column, sort_by_order: this.sort_by_order, per_page: this.per_page,
                };
                apiClient.get(this.url, { params: filter })
                    .then((response) => {
                        vm.local_data = response.data;
                    })
                    .finally(() => {
                        vm.busy_loading = false;
                        this.$emit('data:loaded', vm.local_data);
                        this.navigating = false;
                    });
            } else {
                vm.local_data = { data: orderBy(vm.data, this.sort_by_column, this.sort_by_order) };
                vm.busy_loading = false;
            }
        }, 250),
    }),
    mounted() {
        this.per_page = this.perPage;
        this.local_filter = this.filter;
        this.sort_by_column = this.sortBy;
        this.sort_by_order = this.sortOrder;
        if (this.history && !this.navigating) {
            const urlParams = this.getUrlParameters();
            if (this.loadOnInit || Object.keys(urlParams).length > 0) {
                this.setUrlParams(urlParams, true);
            }
        } else if (!this.navigating && this.loadOnInit && this.autoSearch) {
            this.search();
        }
    },
    created() {
        if (this.history) {
            window.addEventListener('popstate', this.onPopState);
        }
    },
    destroyed() {
        if (this.sort_clicktimer) clearTimeout(this.sort_clicktimer);
        if (this.history) {
            window.removeEventListener('popstate', this.onPopState);
        }
    },
    methods: {
        getUrlParameters,
        onPopState(event) {
            this.navigating = true;
            this.setUrlParams(event.state);
        },
        async setUrlParams(params, setHistory = false) {
            this.navigating = true;
            let hasParams = false;
            if (params) {
                if (Object.keys(params).length > 0) {
                    hasParams = true;
                }
                await Object.entries(params).forEach(([key, value]) => {
                    if (this.local_filter[key] !== undefined) {
                        this.local_filter[key] = value;
                    } else if (key === 'sort_by_column') {
                        this.sort_by_column = value;
                    } else if (key === 'sort_by_order') {
                        this.sort_by_order = value;
                    } else if (key === 'page') {
                        this.page = value;
                    }
                });
            }
            this.search((setHistory && !hasParams));
        },
        setSearchHistory() {
            if (this.history) {
                const filter = {
                    ...this.local_filter, page: this.page, sort_by_column: this.sort_by_column, sort_by_order: this.sort_by_order,
                };
                const urlParams = this.stringifyQuery(filter);
                // eslint-disable-next-line no-restricted-globals
                history.pushState(filter, 'title 1', `?${urlParams}`);
            }
        },
        btnSearchPreviousPage() {
            this.page -= 1;
            this.search(true);
            // var elmnt = document.getElementById("header");
            // document.documentElement.scrollTop = elmnt.offsetHeight;
        },
        btnSearchNextPage() {
            this.page += 1;
            this.search(true);
            // var elmnt = document.getElementById("header");
            // document.documentElement.scrollTop = elmnt.offsetHeight;
        },
        setSortBy(column) {
            if (this.sort_by_column === column) {
                this.sort_clicks += 1;
                if (this.sort_clicks > 1) {
                    if (this.sort_clicktimer) clearTimeout(this.sort_clicktimer);
                    this.sort_by_column = '';
                    this.sort_by_order = '';
                    this.sort_clicks = 0;
                } else {
                    if (this.sort_clicktimer) clearTimeout(this.sort_clicktimer);
                    this.sort_clicktimer = setTimeout(() => {
                        this.sort_clicks = 0;
                    }, 2000);
                    if (this.sort_by_order === 'asc') {
                        this.sort_by_order = 'desc';
                    } else {
                        this.sort_by_order = 'asc';
                    }
                }
            } else {
                if (this.sort_clicktimer) clearTimeout(this.sort_clicktimer);
                this.sort_by_column = column;
                this.sort_by_order = 'asc';
                this.sort_clicks = 0;
            }
            this.page = 1;
            this.search(true);
        },
        rowClick(event, row, col) {
            const parent = event.target.closest('td');
            const aElement = parent.querySelector('a');
            const buttonElement = parent.querySelector('button');
            if (this.has_row_click_listener && event.target.nodeName !== 'A' && event.target.nodeName !== 'BUTTON' && !aElement && !buttonElement && !parent.classList.contains('noclick') && (!Object.prototype.hasOwnProperty.call(col, 'clickable') || col.clickable)) {
                this.$emit('rowclick', row, event);
            }
        },
        reload() {
            this.search();
        },
        stringifyQuery(query) {
            let search = '';
            Object.keys(query).forEach((key) => {
                const value = query[key];
                const encodedKey = this.encodeQueryKey(key);
                if (value == null) {
                    // only null adds the value
                    if (value !== undefined) {
                        search += (search.length ? '&' : '') + encodedKey;
                    }
                    return;
                }
                // keep null values
                const values = Array.isArray(value)
                    ? value.map((v) => v && this.encodeQueryValue(v))
                    : [value && this.encodeQueryValue(value)];

                values.forEach((val) => {
                    // skip undefined values in arrays as if they were not present
                    // smaller code than using filter
                    if (val !== undefined) {
                        // only append & with non-empty search
                        search += (search.length ? '&' : '') + encodedKey;
                        if (val != null) search += '=' + val;
                    }
                });
            });

            return search;
        },
        encodeQueryKey(text) {
            return this.encodeQueryValue(text).replace(/=/g, '%3D');
        },
        encodeQueryValue(text) {
            return encodeURI('' + text)
                .replace(/%7C/g, '|')
                .replace(/%5B/g, '[')
                .replace(/%5D/g, ']')
                .replace(/\+/g, '%2B')
                .replace(/%20/g, '+')
                .replace(/#/g, '%23')
                .replace(/&/g, '%26')
                .replace(/%60/g, '`')
                .replace(/%7B/g, '{')
                .replace(/%7D/g, '}')
                .replace(/%5E/g, '^');
        },
        parseColClasses(colClass, row) {
            if (colClass) {
                if (typeof colClass === 'function') {
                    const rs = colClass(row);
                    if (Array.isArray(rs)) {
                        return rs;
                    }
                    return [rs];
                }
                if (Array.isArray(colClass)) {
                    return colClass;
                }
                return [colClass];
            }
            return [];
        },
        parseRowClasses(rowClass, row) {
            if (rowClass) {
                if (typeof rowClass === 'function') {
                    const rs = rowClass(row);
                    if (Array.isArray(rs)) {
                        return rs;
                    }
                    return [rs];
                }
                if (Array.isArray(rowClass)) {
                    return rowClass;
                }
                return [rowClass];
            }
            return [];
        },
    },
    computed: {
        has_row_click_listener() {
            return this.$listeners && this.$listeners.rowclick;
        },
        history() {
            return this.$listeners && this.$listeners.updateFilter;
        },
        select_per_page() {
            const arr = [
                { id: 10, label: '10 per sida' },
                { id: 25, label: '25 per sida' },
                { id: 100, label: '100 per sida' },
                { id: 250, label: '250 per sida' },
                { id: 1000, label: '1000 per sida' },
            ];
            if (this.showPerPageAll) {
                arr.push({ id: 10000, label: 'Alla' });
            }
            return arr;
        },
    },
};
</script>
<style scoped>
.rednax-table .loading {
    position: relative;
}
.rednax-table .loading:before {
    content: 'Laddar...';
    display: block;
    position: absolute;
    text-align: center;
    padding: 70px 10px;
    height: 100%;
    width: 100%;
    font-size: 1rem;
    background-color: rgba(255,255,255, .9);
}
</style>
